import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildReportsMd } from "@/lib/mdBuilders";
import { useAuth } from "@/context/AuthContext";
import { Download, FileText, Calendar, Share2, Link as LinkIcon, Copy, Trash2, Check, Palette, Lock, Sparkles, Mail, Image as ImageIcon, Twitter, ClipboardList, Upload, Send, Zap } from "lucide-react";
import { toast } from "sonner";
import { CreditActionButton } from "@/components/CreditActionButton";
import { MetricInfo } from "@/components/MetricInfo";

const REPORT_METRIC = {
    "Visibility": "visibility_index",
    "Est. Traffic": "estimated_traffic",
    "Keywords": "tracked_keywords",
    "Top 10": "top3_top10",
    "Toxic Links": "toxic_backlinks",
    "AI Overviews": "ai_overview_coverage",
    "Ref. Domains": "referring_domains",
    "Audit Score": "health_score",
};

const FREE_SHARE_LIMIT = 3;

export default function Reports() {
    const project = useProject();
    const { user, refresh } = useAuth();
    const isPro = user?.is_admin || ["pro", "solo", "agency"].includes(user?.tier);
    const [rep, setRep] = useState(null);
    const [shares, setShares] = useState([]);
    const [brand, setBrand] = useState({
        brand_name: "",
        brand_color: "#38CFFF",
        logo_url: "",
        footer_note: "",
        expires_days: 30,
    });
    const [creating, setCreating] = useState(false);
    const [copied, setCopied] = useState(null);
    const [emailSchedule, setEmailSchedule] = useState({ enabled: false, day: "monday", recipient: "" });
    const [savingSchedule, setSavingSchedule] = useState(false);
    const [sendingNow, setSendingNow] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);
    const [activity, setActivity] = useState(null);
    const [activityDays, setActivityDays] = useState(30);
    const [activityDownloading, setActivityDownloading] = useState(false);
    const [digest, setDigest] = useState(null);
    const [digestGeneratedAt, setDigestGeneratedAt] = useState(null);

    const loadShares = () => {
        if (!project) return;
        client.get(`/projects/${project.id}/shares`).then((r) => setShares(r.data));
    };

    useEffect(() => {
        if (!project) return;
        client.get(`/projects/${project.id}/report`).then((r) => setRep(r.data));
        setBrand((b) => ({ ...b, brand_name: project.name || "" }));
        loadShares();
        refresh && refresh();
        // eslint-disable-next-line
    }, [project]);

    // Load monthly activity summary
    useEffect(() => {
        if (!project) return;
        client
            .get(`/projects/${project.id}/activity-report?days=${activityDays}`)
            .then((r) => setActivity(r.data))
            .catch(() => setActivity(null));
    }, [project, activityDays]);

    // Load per-project brand preset and pre-fill the branding form.
    const [logoInfo, setLogoInfo] = useState({ mime: "", size: 0, url: "" });
    useEffect(() => {
        if (!project) return;
        client
            .get(`/projects/${project.id}/brand`)
            .then((r) => {
                const b = r.data || {};
                setBrand((prev) => ({
                    ...prev,
                    brand_name: b.brand_name || prev.brand_name || project.name || "",
                    brand_color: b.brand_color || prev.brand_color || "#38CFFF",
                    logo_url: b.logo_url || prev.logo_url || "",
                    footer_note: b.footer_note || prev.footer_note || "",
                }));
                setLogoInfo({
                    mime: b.logo_upload_mime || "",
                    size: b.logo_upload_size || 0,
                    url: b.logo_upload_url || "",
                });
            })
            .catch(() => {});
    }, [project]);

    const logoInputRef = React.useRef(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const uploadLogo = async (file) => {
        if (!file) return;
        if (file.size > 500 * 1024) {
            toast.error("Logo must be under 500 KB");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setLogoUploading(true);
        try {
            const r = await client.post(`/projects/${project.id}/brand/logo`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLogoInfo({ mime: r.data.mime, size: r.data.size, url: r.data.logo_upload_url + "&t=" + Date.now() });
            toast.success("Logo uploaded · will appear in every branded PDF");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setLogoUploading(false);
        }
    };
    const removeLogo = async () => {
        try {
            await client.delete(`/projects/${project.id}/brand/logo`);
            setLogoInfo({ mime: "", size: 0, url: "" });
            toast.success("Logo removed");
        } catch (e) {
            toast.error("Failed to remove logo");
        }
    };

    const [brandSaving, setBrandSaving] = useState(false);
    const saveBrandPreset = async () => {
        setBrandSaving(true);
        try {
            await client.put(`/projects/${project.id}/brand`, {
                brand_name: brand.brand_name,
                brand_color: brand.brand_color,
                logo_url: brand.logo_url,
                footer_note: brand.footer_note,
            });
            toast.success("Brand preset saved · will auto-apply to every export");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save brand preset");
        } finally {
            setBrandSaving(false);
        }
    };

    const downloadActivityPDF = async () => {
        setActivityDownloading(true);
        try {
            const params = new URLSearchParams({
                days: String(activityDays),
                brand_name: brand.brand_name || project.name,
                brand_color: brand.brand_color,
                ...(brand.footer_note ? { footer_note: brand.footer_note } : {}),
            });
            const r = await client.get(`/projects/${project.id}/activity-report.pdf?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `activity-${project.domain}-${new Date().toISOString().slice(0, 10)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Activity report downloaded");
        } catch {
            toast.error("Failed to download activity report");
        } finally {
            setActivityDownloading(false);
        }
    };

    // Load schedule for the most recent share when it changes
    useEffect(() => {
        if (!shares.length || !isPro) return;
        const first = shares[0];
        client.get(`/shares/${first.token}/schedule`).then((r) => {
            setEmailSchedule({
                enabled: !!r.data.enabled,
                day: r.data.day || "monday",
                recipient: r.data.recipient || user?.email || "",
            });
        }).catch(() => {});
        // eslint-disable-next-line
    }, [shares.length, isPro]);

    const activeToken = shares[0]?.token;

    const saveSchedule = async () => {
        if (!activeToken) { toast.error("Create a share first"); return; }
        if (!emailSchedule.recipient) { toast.error("Recipient email required"); return; }
        setSavingSchedule(true);
        try {
            await client.post(`/shares/${activeToken}/schedule`, emailSchedule);
            toast.success("Schedule saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSavingSchedule(false);
        }
    };

    const sendNow = async () => {
        if (!activeToken) { toast.error("Create a share first"); return; }
        setSendingNow(true);
        try {
            const r = await client.post(`/shares/${activeToken}/send-now`);
            if (r.data.email_live) {
                toast.success(`Sent to ${r.data.recipient}`);
            } else {
                toast.success(`MOCKED send to ${r.data.recipient} (set RESEND_API_KEY to send real)`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setSendingNow(false);
        }
    };

    const openPortal = async () => {
        setPortalLoading(true);
        try {
            const r = await client.post("/billing/portal", { return_url: `${window.location.origin}/app/reports` });
            window.location.href = r.data.portal_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Portal unavailable");
            setPortalLoading(false);
        }
    };

    const generateDigest = async () => {
        try {
            const r = await client.get(`/projects/${project.id}/ai-digest`);
            setDigest(r.data);
            setDigestGeneratedAt(new Date().toISOString());
            toast.success("Weekly digest generated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Digest failed");
        }
    };

    const downloadJSON = () => {
        const blob = new Blob([JSON.stringify(rep, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `rankforge-report-${project.domain}-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("JSON report downloaded");
    };

    const downloadPDF = async () => {
        const params = new URLSearchParams({
            brand_name: brand.brand_name || project.name,
            brand_color: brand.brand_color,
            ...(brand.footer_note ? { footer_note: brand.footer_note } : {}),
        });
        try {
            const r = await client.get(`/projects/${project.id}/report.pdf?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `rankforge-${project.domain}-${new Date().toISOString().slice(0, 10)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch {
            toast.error("PDF download failed");
        }
    };

    const createShare = async () => {
        setCreating(true);
        try {
            const payload = { ...brand, project_id: project.id };
            if (!isPro) delete payload.logo_url;
            const r = await client.post(`/projects/${project.id}/shares`, payload);
            setShares((prev) => [r.data, ...prev]);
            toast.success("Shareable link created");
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.error(e.response.data.detail || "Pro feature");
            } else {
                toast.error("Failed to create link");
            }
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (token) => {
        await client.delete(`/shares/${token}`);
        setShares((prev) => prev.filter((s) => s.token !== token));
        toast.success("Link revoked");
    };

    const publicUrl = (token) => `${window.location.origin}/share/${token}`;
    const socialUrl = (token) => `${process.env.REACT_APP_BACKEND_URL}/api/public/shares/${token}/social`;
    const ogUrl = (token) => `${process.env.REACT_APP_BACKEND_URL}/api/public/shares/${token}/og.png`;

    const copyLink = async (token, kind = "public") => {
        try {
            const url = kind === "social" ? socialUrl(token) : publicUrl(token);
            await navigator.clipboard.writeText(url);
            setCopied(`${token}:${kind}`);
            setTimeout(() => setCopied(null), 1600);
            toast.success(kind === "social" ? "Social share URL copied" : "Copied to clipboard");
        } catch {
            toast.error("Copy failed");
        }
    };

    if (!project || !rep) return <div className="text-muted-foreground font-mono text-sm">loading report…</div>;

    return (
        <div className="space-y-6" data-testid="reports-page">
            {!isPro && (
                <div className="border border-primary/60 bg-primary/5 p-5 flex items-start gap-4" data-testid="upgrade-banner">
                    <div className="w-9 h-9 border border-primary grid place-items-center shrink-0">
                        <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-medium">You're on the Starter plan</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            You have <span className="text-foreground font-mono">{shares.length}/{FREE_SHARE_LIMIT}</span> share links. Upgrade to Pro for custom logos, unlimited shares, priority AI, and scheduled weekly PDF emails.
                        </div>
                    </div>
                    <Link to="/pricing" data-testid="upgrade-link" className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 shrink-0">
                        Upgrade →
                    </Link>
                </div>
            )}

            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Automated reporting</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Reports</h1>
                    <p className="text-sm text-muted-foreground mt-2">White-label PDF exports & shareable client dashboards.</p>
                </div>
                <div className="flex gap-2">
                    {isPro && (
                        <button data-testid="manage-billing-btn" onClick={openPortal} disabled={portalLoading}
                                className="px-4 py-2 text-sm border border-border hover:border-primary flex items-center gap-2 disabled:opacity-50">
                            <Sparkles className="w-4 h-4" /> {portalLoading ? "Opening…" : "Manage billing"}
                        </button>
                    )}
                    <button data-testid="download-json-btn" onClick={downloadJSON}
                            className="px-4 py-2 text-sm border border-border hover:border-primary flex items-center gap-2">
                        <Download className="w-4 h-4" /> JSON
                    </button>
                    <DownloadMdButton
                        filenamePrefix={`inphase-report-${project?.domain || "project"}`}
                        buildMarkdown={() => buildReportsMd({ project, rep })}
                        disabled={!rep}
                        testId="download-report-md"
                        label=".md"
                    />
                    <button data-testid="download-report-btn" onClick={downloadPDF}
                            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Export PDF
                    </button>
                </div>
            </div>

            {/* Weekly AI Digest — user-triggered (iter34 replaced weekly cron) */}
            <div className="border border-border bg-card/40" data-testid="weekly-digest-panel">
                <div className="p-6 border-b border-border flex items-center gap-3">
                    <Zap className="w-4 h-4 text-primary" />
                    <div className="overline">Weekly AI digest</div>
                    <span className="text-[10px] font-mono text-muted-foreground/70 ml-auto">
                        Cron paused · fire manually
                    </span>
                </div>
                <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
                    <div className="max-w-2xl">
                        <p className="text-sm text-muted-foreground">
                            Claude-generated TL;DR + wins, risks, and this week's action items for{" "}
                            <span className="font-mono text-foreground">{project.domain}</span>.
                            Fire it on demand — the weekly auto-email cron is off, nothing sends without your click.
                        </p>
                        {digestGeneratedAt && (
                            <div className="text-[10px] font-mono text-muted-foreground/70 mt-2">
                                Last generated: {new Date(digestGeneratedAt).toLocaleString()}
                                {digest?.data_source && ` · source: ${digest.data_source}`}
                            </div>
                        )}
                    </div>
                    <CreditActionButton
                        actionKey="digest"
                        testid="send-weekly-digest-btn"
                        onConfirm={generateDigest}
                        description={`Generates this week's AI digest for ${project.domain}. Pulls live dashboard + backlinks + keywords data and runs Claude Sonnet on top.`}
                    >
                        <span className="inline-flex items-center gap-1">
                            <Send className="w-4 h-4" /> {digest ? "Regenerate digest" : "Generate this week's digest"}
                        </span>
                    </CreditActionButton>
                </div>
                {digest?.digest_text && (
                    <div className="border-t border-border p-6" data-testid="digest-output">
                        <pre className="whitespace-pre-wrap text-sm font-sans text-foreground leading-relaxed">
                            {digest.digest_text}
                        </pre>
                    </div>
                )}
                {digest && !digest.digest_text && (
                    <div className="border-t border-border p-6 text-xs text-muted-foreground" data-testid="digest-empty">
                        AI generation returned no text this run. Try again in a minute.
                    </div>
                )}
            </div>

            {/* Branding + create share */}
            <div className="border border-border bg-card/40" data-testid="branding-panel">
                <div className="p-6 border-b border-border flex items-center gap-3">
                    <Palette className="w-4 h-4 text-primary" />
                    <div className="overline">White-label branding</div>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                        <label className="text-xs text-muted-foreground block mb-2">Brand name</label>
                        <input data-testid="brand-name-input" value={brand.brand_name}
                               onChange={(e) => setBrand({ ...brand, brand_name: e.target.value })}
                               className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-xs text-muted-foreground block mb-2 flex items-center gap-1">
                            Logo {!isPro && <Lock className="w-3 h-3 text-primary" />}
                        </label>
                        <div className="flex items-center gap-2">
                            {logoInfo.url ? (
                                <>
                                    <img
                                        data-testid="logo-preview"
                                        src={`${process.env.REACT_APP_BACKEND_URL}${logoInfo.url}`}
                                        alt="Uploaded logo"
                                        className="h-9 w-auto bg-white/5 border border-border object-contain px-2"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => logoInputRef.current?.click()}
                                        disabled={!isPro || logoUploading}
                                        className="px-3 py-2 text-xs border border-border hover:bg-primary/10 flex items-center gap-1 disabled:opacity-50"
                                        data-testid="logo-replace-btn"
                                    >
                                        <Upload className="w-3 h-3" /> Replace
                                    </button>
                                    <button
                                        type="button"
                                        onClick={removeLogo}
                                        disabled={!isPro}
                                        className="px-3 py-2 text-xs border border-border hover:bg-red-500/10 hover:text-red-500 flex items-center gap-1 disabled:opacity-50"
                                        data-testid="logo-remove-btn"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => logoInputRef.current?.click()}
                                        disabled={!isPro || logoUploading}
                                        className="px-3 py-2 text-xs border border-primary/60 text-primary hover:bg-primary/10 flex items-center gap-1 disabled:opacity-50"
                                        data-testid="logo-upload-btn"
                                    >
                                        <Upload className="w-3 h-3" />
                                        {logoUploading ? "Uploading…" : (isPro ? "Upload PNG/JPEG" : "Pro feature")}
                                    </button>
                                    <input
                                        data-testid="logo-url-input"
                                        value={brand.logo_url}
                                        disabled={!isPro}
                                        onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })}
                                        placeholder="…or paste https URL"
                                        className="flex-1 bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary disabled:opacity-50"
                                    />
                                </>
                            )}
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                className="hidden"
                                data-testid="logo-file-input"
                                onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                            />
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                            Auto-embedded in every branded PDF · max 500 KB
                        </p>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-2">Accent color</label>
                        <div className="flex gap-2">
                            <input data-testid="brand-color-input" type="color" value={brand.brand_color}
                                   onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })}
                                   className="w-12 h-9 bg-input border border-border cursor-pointer" />
                            <input value={brand.brand_color}
                                   onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })}
                                   className="flex-1 bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-2">Footer note</label>
                        <input data-testid="footer-note-input" value={brand.footer_note}
                               onChange={(e) => setBrand({ ...brand, footer_note: e.target.value })}
                               placeholder="Prepared by Acme Agency"
                               className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground block mb-2">Link expires</label>
                        <select data-testid="expires-select" value={brand.expires_days}
                                onChange={(e) => setBrand({ ...brand, expires_days: parseInt(e.target.value) })}
                                className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary">
                            <option value={7}>7 days</option>
                            <option value={30}>30 days</option>
                            <option value={90}>90 days</option>
                            <option value={365}>1 year</option>
                        </select>
                    </div>
                </div>
                <div className="px-6 pb-6 flex items-center gap-3 flex-wrap">
                    <button data-testid="create-share-btn" onClick={createShare} disabled={creating}
                            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50">
                        <Share2 className="w-4 h-4" /> {creating ? "Creating…" : "Create shareable link"}
                    </button>
                    <button
                        data-testid="save-brand-preset-btn"
                        onClick={saveBrandPreset}
                        disabled={brandSaving}
                        className="px-4 py-2 text-sm border border-primary/60 text-primary hover:bg-primary/10 flex items-center gap-2 disabled:opacity-50"
                    >
                        <Palette className="w-4 h-4" />
                        {brandSaving ? "Saving…" : "Save as project default"}
                    </button>
                    <span className="text-xs text-muted-foreground font-mono">
                        Applied automatically to every PDF export &amp; new share.
                    </span>
                </div>
            </div>

            {/* Client-facing activity report */}
            <div className="border border-border bg-card/40" data-testid="activity-report-panel">
                <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <ClipboardList className="w-4 h-4 text-primary" />
                        <div>
                            <div className="overline">Client-facing report</div>
                            <div className="text-sm font-medium mt-1">What we did this month</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-muted-foreground">Window</label>
                        <select
                            data-testid="activity-days-select"
                            value={activityDays}
                            onChange={(e) => setActivityDays(parseInt(e.target.value))}
                            className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                        >
                            <option value={7}>Last 7 days</option>
                            <option value={30}>Last 30 days</option>
                            <option value={90}>Last 90 days</option>
                            <option value={180}>Last 6 months</option>
                            <option value={365}>Last 12 months</option>
                        </select>
                        <button
                            data-testid="download-activity-pdf-btn"
                            onClick={downloadActivityPDF}
                            disabled={activityDownloading}
                            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                        >
                            <FileText className="w-4 h-4" />
                            {activityDownloading ? "Building PDF…" : "Download PDF"}
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    {!activity ? (
                        <div className="text-muted-foreground font-mono text-sm">loading activity…</div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="activity-kpis">
                            {[
                                { label: "Disavow exports", val: activity.totals.disavow_exports, testid: "kpi-disavow-exports", metricKey: "disavow_exports" },
                                { label: "Toxic domains", val: activity.totals.toxic_domains_disavowed, testid: "kpi-toxic-domains", metricKey: "toxic_backlinks" },
                                { label: "Briefs delivered", val: activity.totals.briefs_delivered, testid: "kpi-briefs", metricKey: "briefs_delivered" },
                                { label: "Audits run", val: activity.totals.audits_run, testid: "kpi-audits", metricKey: "audits_run" },
                                { label: "Keywords gained", val: activity.totals.winners, testid: "kpi-winners", metricKey: "keywords_gained" },
                                { label: "Keywords lost", val: activity.totals.losers, testid: "kpi-losers", metricKey: "keywords_lost" },
                            ].map((k) => (
                                <div key={k.label} data-testid={k.testid} className="border border-border p-3">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">{k.label} <MetricInfo metricKey={k.metricKey} /></div>
                                    <div className="text-2xl font-display mt-1">{k.val.toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-4 font-mono">
                        Bundles disavow exports, content briefs, audits, and rank changes into a branded PDF you can send to clients.
                    </p>
                </div>
            </div>

            {/* Existing shares */}
            {shares.length > 0 && (
                <div className="border border-border bg-card/40" data-testid="shares-list">
                    <div className="p-4 border-b border-border">
                        <div className="overline">Active shares</div>
                        <div className="text-xs text-muted-foreground mt-1">{shares.length} link{shares.length > 1 ? "s" : ""} · client dashboards live and public</div>
                    </div>
                    <div className="divide-y divide-border/50">
                        {shares.map((s) => (
                            <div key={s.token} className="p-4 space-y-3" data-testid={`share-${s.token}`}>
                                <div className="flex items-center gap-4">
                                    <div className="w-9 h-9 border grid place-items-center shrink-0" style={{ borderColor: s.brand_color + "80", color: s.brand_color }}>
                                        <LinkIcon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{s.brand_name}</div>
                                        <div className="text-xs text-muted-foreground font-mono truncate">{publicUrl(s.token)}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground hidden md:block">
                                        expires {new Date(s.expires_at).toLocaleDateString()}
                                    </div>
                                    <button data-testid={`copy-${s.token}`} onClick={() => copyLink(s.token, "public")}
                                            className="p-2 text-muted-foreground hover:text-primary" title="Copy dashboard URL">
                                        {copied === `${s.token}:public` ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                    <button data-testid={`revoke-${s.token}`} onClick={() => revoke(s.token)}
                                            className="p-2 text-muted-foreground hover:text-destructive" title="Revoke link">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex items-stretch gap-3 pl-12">
                                    <a href={ogUrl(s.token)} target="_blank" rel="noreferrer"
                                       data-testid={`og-preview-${s.token}`}
                                       className="w-32 h-16 border border-border overflow-hidden shrink-0 hover:border-primary transition-colors">
                                        <img src={ogUrl(s.token)} alt="Social card preview"
                                             className="w-full h-full object-cover" loading="lazy" />
                                    </a>
                                    <div className="flex-1 flex flex-wrap gap-2 items-center">
                                        <button data-testid={`copy-social-${s.token}`} onClick={() => copyLink(s.token, "social")}
                                                className="text-xs border border-border px-3 py-1.5 hover:border-primary flex items-center gap-1.5">
                                            <Twitter className="w-3 h-3" />
                                            {copied === `${s.token}:social` ? "Copied social URL" : "Copy social share URL"}
                                        </button>
                                        <a data-testid={`download-og-${s.token}`} href={ogUrl(s.token)} download={`${s.brand_name || "social"}-card.png`}
                                           className="text-xs border border-border px-3 py-1.5 hover:border-primary flex items-center gap-1.5">
                                            <ImageIcon className="w-3 h-3" /> Download card (1200×630)
                                        </a>
                                        <span className="text-xs text-muted-foreground font-mono">unfurls on Twitter · LinkedIn · Slack</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Report preview */}
            <div className="border border-border bg-card/40 p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 border border-primary/60 grid place-items-center">
                        <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <div className="font-display text-xl tracking-tight">{rep.project.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{rep.project.domain} · generated {new Date(rep.generated_at).toLocaleString()}</div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[
                        ["Visibility", rep.summary.visibility + "%"],
                        ["Est. Traffic", rep.summary.estimated_traffic.toLocaleString()],
                        ["Keywords", rep.summary.total_keywords],
                        ["Top 10", rep.summary.top10],
                        ["Toxic Links", rep.summary.toxic_links],
                        ["AI Overviews", rep.summary.ai_overviews],
                        ["Ref. Domains", rep.backlinks.referring_domains.toLocaleString()],
                        ["Audit Score", rep.audit.health_score],
                    ].map(([l, v]) => (
                        <div key={l} className="border border-border/60 p-4">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">{l} <MetricInfo metricKey={REPORT_METRIC[l]} /></div>
                            <div className="font-mono text-xl mt-1">{v}</div>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border border-border/60 p-4">
                        <div className="overline">Site health</div>
                        <div className="text-sm mt-3">Errors: <span className="font-mono text-destructive">{rep.audit.errors}</span></div>
                        <div className="text-sm">Warnings: <span className="font-mono text-warning">{rep.audit.warnings}</span></div>
                        <div className="text-sm">Notices: <span className="font-mono text-secondary">{rep.audit.notices}</span></div>
                    </div>
                    <div className="border border-border/60 p-4">
                        <div className="overline">Core Web Vitals</div>
                        <div className="text-sm mt-3">LCP: <span className="font-mono">{rep.audit.core_web_vitals.lcp}s</span></div>
                        <div className="text-sm">CLS: <span className="font-mono">{rep.audit.core_web_vitals.cls}</span></div>
                        <div className="text-sm">INP: <span className="font-mono">{rep.audit.core_web_vitals.inp}ms</span></div>
                    </div>
                    <div className="border border-border/60 p-4">
                        <div className="overline">Backlinks</div>
                        <div className="text-sm mt-3">Total: <span className="font-mono">{rep.backlinks.backlinks.toLocaleString()}</span></div>
                        <div className="text-sm">DR: <span className="font-mono">{rep.backlinks.domain_rating}</span></div>
                        <div className="text-sm">Toxic: <span className="font-mono text-destructive">{rep.backlinks.toxic_domains}</span></div>
                    </div>
                </div>
            </div>

            <div className="border border-border bg-card/40 p-6" data-testid="email-schedule-panel">
                <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                    <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-primary" />
                        <div>
                            <div className="overline">Scheduled weekly PDF emails</div>
                            <div className="text-xs text-muted-foreground mt-1">Every {emailSchedule.day}, a branded PDF snapshot lands in your client's inbox.</div>
                        </div>
                    </div>
                    {!isPro && (
                        <Link to="/pricing" className="text-xs text-primary flex items-center gap-1 hover:underline">
                            <Lock className="w-3 h-3" /> Unlock with Pro
                        </Link>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="text-xs text-muted-foreground flex items-center gap-2 border border-border bg-input px-3 py-2 cursor-pointer">
                        <input data-testid="email-enabled-toggle" type="checkbox" checked={emailSchedule.enabled}
                               disabled={!isPro}
                               onChange={(e) => setEmailSchedule({ ...emailSchedule, enabled: e.target.checked })} />
                        Enabled
                    </label>
                    <select data-testid="email-day-select" value={emailSchedule.day} disabled={!isPro || !emailSchedule.enabled}
                            onChange={(e) => setEmailSchedule({ ...emailSchedule, day: e.target.value })}
                            className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary disabled:opacity-50">
                        {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((d) => (
                            <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                        ))}
                    </select>
                    <input data-testid="email-recipient-input" type="email" value={emailSchedule.recipient}
                           disabled={!isPro || !emailSchedule.enabled}
                           onChange={(e) => setEmailSchedule({ ...emailSchedule, recipient: e.target.value })}
                           placeholder="client@company.com"
                           className="bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary disabled:opacity-50" />
                </div>
                {isPro && (
                    <div className="mt-4 flex gap-2 flex-wrap items-center">
                        <button data-testid="save-schedule-btn" onClick={saveSchedule} disabled={savingSchedule}
                                className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                            {savingSchedule ? "Saving…" : "Save schedule"}
                        </button>
                        <button data-testid="send-now-btn" onClick={sendNow} disabled={sendingNow}
                                className="border border-border px-4 py-2 text-sm hover:border-primary disabled:opacity-50 flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5" /> {sendingNow ? "Sending…" : "Send test now"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
